import React, {
  ReactNode,
  useState,
  useContext,
  createContext,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import {
  IBucketList,
  IBucketListItem,
  IBucketListByRegion,
  AddBucketListItemRequest,
  BucketListRegion
} from '../../Models/bucket-list/bucket-list.model';
import bucketListService from '../../services/bucket-list/bucket-list.service';

export interface BucketListContextType {
  bucketList: IBucketList | null;
  isLoading: boolean;
  error: string | null;
  byRegion: IBucketListByRegion[];
  addItem: (item: AddBucketListItemRequest) => Promise<void>;
  updateItem: (
    itemId: string,
    updates: Partial<AddBucketListItemRequest>
  ) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  addItemToTrip: (itemId: string, tripId: string) => Promise<void>;
  refreshBucketList: () => void;
}

const BucketListContext = createContext<BucketListContextType | undefined>(
  undefined
);

const REGION_ORDER = Object.values(BucketListRegion);

export const BucketListProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [bucketList, setBucketList] = useState<IBucketList | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState<boolean>(false);

  const fetchBucketList = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await bucketListService.getBucketList();
      setBucketList(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load bucket list');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBucketList();
  }, [fetchBucketList, refresh]);

  const byRegion = useMemo((): IBucketListByRegion[] => {
    if (!bucketList?.items?.length) return [];
    const regionMap = new Map<string, IBucketListItem[]>();
    bucketList.items.forEach((item) => {
      const region = item.region || BucketListRegion.Unknown;
      if (!regionMap.has(region)) {
        regionMap.set(region, []);
      }
      regionMap.get(region)!.push(item);
    });
    return REGION_ORDER.filter((r) => regionMap.has(r)).map((region) => ({
      region: region as BucketListRegion,
      items: regionMap.get(region)!
    }));
  }, [bucketList]);

  const refreshBucketList = useCallback(() => {
    setRefresh((r) => !r);
  }, []);

  const addItem = useCallback(
    async (item: AddBucketListItemRequest) => {
      await bucketListService.addItem(item);
      refreshBucketList();
    },
    [refreshBucketList]
  );

  const updateItem = useCallback(
    async (itemId: string, updates: Partial<AddBucketListItemRequest>) => {
      await bucketListService.updateItem(itemId, updates);
      refreshBucketList();
    },
    [refreshBucketList]
  );

  const removeItem = useCallback(
    async (itemId: string) => {
      await bucketListService.removeItem(itemId);
      refreshBucketList();
    },
    [refreshBucketList]
  );

  const addItemToTrip = useCallback(async (itemId: string, tripId: string) => {
    await bucketListService.addItemToTrip(itemId, tripId);
  }, []);

  return (
    <BucketListContext.Provider
      value={{
        bucketList,
        isLoading,
        error,
        byRegion,
        addItem,
        updateItem,
        removeItem,
        addItemToTrip,
        refreshBucketList
      }}
    >
      {children}
    </BucketListContext.Provider>
  );
};

export const useBucketList = (): BucketListContextType => {
  const context = useContext(BucketListContext);
  if (context === undefined) {
    throw new Error('useBucketList must be used within a BucketListProvider');
  }
  return context;
};
